/**
 * The two items every row menu starts with, and the one it offers when there
 * is something worth copying — list engine v2's "Row ⋯ menu: Open, Open in new
 * tab, then the record's Next-action verb…".
 *
 * Here rather than in each screen because the first two items are a promise:
 * wherever you right-click in Rowted, the top of the menu is the same two
 * things in the same order with the same shortcuts. Five screens writing that
 * themselves is five chances for one of them to put Open second.
 */
import type { MenuItem } from '../components/data-list.js'

/** Open, and open in a new tab. Always first, always in this order. */
export function openItems(
  path: string, navigate: (to: string) => void,
): MenuItem[] {
  return [
    { key: 'open', label: 'Open', hint: '⏎', onSelect: () => navigate(path) },
    {
      key: 'open-tab',
      label: 'Open in new tab',
      hint: '⌘click',
      // `noopener` because the opened tab is ours but the habit is the point:
      // a window handed `window.opener` can navigate the one that opened it.
      onSelect: () => window.open(path, '_blank', 'noopener'),
    },
  ]
}

/**
 * Copy one field to the clipboard — an email, an address, a reference.
 *
 * Returns nothing when there is nothing to copy, so a menu never offers "Copy
 * email" on a contact that has no email. The clipboard API is refused outside
 * a secure context and in some embedded browsers, so the failure is swallowed:
 * a menu item that throws into the console is worse than one that quietly does
 * not work in a setting where nothing could have.
 */
export function copyItem(
  key: string, label: string, value: unknown, separatorBefore = false,
): MenuItem[] {
  const text = typeof value === 'string' ? value.trim() : ''
  if (!text) return []

  return [{
    key,
    label,
    separatorBefore,
    onSelect: () => { void navigator.clipboard?.writeText(text).catch(() => {}) },
  }]
}
